import { useEffect, useMemo, useState, type DragEvent as ReactDragEvent } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';

import {
    createGenerationProfile,
    deleteGenerationProfile,
    getGenerationProfileDetail,
    getGenerationProfileList,
    type GenerationProfileDetail,
    type GenerationProfileListItem,
    type GenerationProfileUpsertParams,
    updateGenerationProfile,
} from '../admin/adminMockTestDataProvider';
import { SegmentedTabs, type SegmentedTabOption } from '../admin/SegmentedTabs';
import { formatApiDateTime } from '../admin/dateTimeUtils';
import {
    examNames,
    examTaskCategories,
    getExamTaskTypeLabel,
    getExamTaskTypesByExamName,
    type ExamName,
} from '../examTasks/examTaskTypes';

type DraftGroup = {
    localId: string;
    id?: string;
    sequenceNumber: number;
    examTaskType: string;
    plannedItemCount: number;
};

type DraftSection = {
    localId: string;
    id?: string;
    sequenceNumber: number;
    sectionCategory: string;
    taskTypes: DraftGroup[];
};

type DraftProfile = {
    id?: string;
    name: string;
    examName: ExamName;
    createdAt?: string;
    updatedAt?: string;
    sections: DraftSection[];
};

type DragState =
    | { type: 'section'; sectionLocalId: string }
    | { type: 'group'; sectionLocalId: string; groupLocalId: string }
    | null;

function createLocalId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    return `local-${Math.random().toString(36).slice(2, 10)}`;
}

function formatDateTime(value?: string) {
    return formatApiDateTime(value);
}

function sortBySequence<T extends { sequenceNumber: number; localId?: string }>(items: T[]) {
    return [...items].sort((left, right) => {
        if (left.sequenceNumber !== right.sequenceNumber) {
            return left.sequenceNumber - right.sequenceNumber;
        }

        return String(left.localId ?? '').localeCompare(String(right.localId ?? ''));
    });
}

function moveItem<T>(items: T[], fromIndex: number, toIndex: number) {
    const next = [...items];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
}

function resequenceGroups(groups: DraftGroup[]) {
    return groups.map((group, index) => ({
        ...group,
        sequenceNumber: index + 1,
    }));
}

function resequenceSections(sections: DraftSection[]) {
    return sections.map((section, index) => ({
        ...section,
        sequenceNumber: index + 1,
        taskTypes: resequenceGroups(sortBySequence(section.taskTypes)),
    }));
}

function getAllowedTaskTypes(examName: ExamName, sectionCategory: string) {
    return getExamTaskTypesByExamName(examName).filter((taskType) => taskType.category === sectionCategory);
}

function getFirstUnusedSectionCategory(currentSections: DraftSection[]) {
    const usedCategories = new Set(currentSections.map((section) => section.sectionCategory));
    return examTaskCategories.find((category) => !usedCategories.has(category.id))?.id ?? examTaskCategories[0]?.id ?? 'Speaking';
}

function createDraftGroup(examName: ExamName, sectionCategory: string, currentGroups: DraftGroup[] = []): DraftGroup {
    const allowedTaskTypes = getAllowedTaskTypes(examName, sectionCategory);
    const usedTaskTypes = new Set(currentGroups.map((group) => group.examTaskType));
    const nextTaskType = allowedTaskTypes.find((taskType) => !usedTaskTypes.has(taskType.apiType)) ?? allowedTaskTypes[0];

    return {
        localId: createLocalId(),
        sequenceNumber: currentGroups.length + 1,
        examTaskType: nextTaskType?.apiType ?? '',
        plannedItemCount: 1,
    };
}

function createDraftSection(examName: ExamName, currentSections: DraftSection[] = []): DraftSection {
    const sectionCategory = getFirstUnusedSectionCategory(currentSections);
    const section: DraftSection = {
        localId: createLocalId(),
        sequenceNumber: currentSections.length + 1,
        sectionCategory,
        taskTypes: [],
    };

    section.taskTypes = [createDraftGroup(examName, sectionCategory)];
    return section;
}

function emptyDraftProfile(examName: ExamName): DraftProfile {
    const firstSection = createDraftSection(examName, []);
    return {
        name: '',
        examName,
        sections: [firstSection],
    };
}

function normalizeSectionsForExamName(sections: DraftSection[], examName: ExamName) {
    return sections.map((section) => {
        const allowedTaskTypes = getAllowedTaskTypes(examName, section.sectionCategory);
        const allowedTaskTypeKeys = new Set(allowedTaskTypes.map((taskType) => taskType.apiType));
        const filteredGroups = section.taskTypes.filter((group) => allowedTaskTypeKeys.has(group.examTaskType));
        const nextGroups = filteredGroups.length > 0 ? filteredGroups : [createDraftGroup(examName, section.sectionCategory)];

        return {
            ...section,
            taskTypes: sortBySequence(nextGroups),
        };
    });
}

function toDraftProfile(detail: GenerationProfileDetail): DraftProfile {
    return {
        id: detail.id,
        name: detail.name,
        examName: detail.examName as ExamName,
        createdAt: detail.createdAt,
        updatedAt: detail.updatedAt,
        sections: sortBySequence(
            detail.sections.map((section) => ({
                localId: createLocalId(),
                id: section.id,
                sequenceNumber: section.sequenceNumber,
                sectionCategory: section.sectionCategory,
                taskTypes: sortBySequence(
                    section.taskTypes.map((group) => ({
                        localId: createLocalId(),
                        id: group.id,
                        sequenceNumber: group.sequenceNumber,
                        examTaskType: group.examTaskType,
                        plannedItemCount: group.plannedItemCount,
                    })),
                ),
            })),
        ),
    };
}

function cloneDraftProfile(draft: DraftProfile, name: string): DraftProfile {
    return {
        name,
        examName: draft.examName,
        sections: sortBySequence(draft.sections).map((section) => ({
            localId: createLocalId(),
            sequenceNumber: section.sequenceNumber,
            sectionCategory: section.sectionCategory,
            taskTypes: sortBySequence(section.taskTypes).map((group) => ({
                localId: createLocalId(),
                sequenceNumber: group.sequenceNumber,
                examTaskType: group.examTaskType,
                plannedItemCount: group.plannedItemCount,
            })),
        })),
    };
}

function buildCopyName(existingProfiles: GenerationProfileListItem[], sourceName: string) {
    const trimmed = sourceName.trim() || 'Generation Profile';
    const takenNames = new Set(existingProfiles.map((profile) => profile.name.toLowerCase()));
    const baseName = `${trimmed} Copy`;
    if (!takenNames.has(baseName.toLowerCase())) {
        return baseName;
    }

    let suffix = 2;
    while (takenNames.has(`${baseName} ${suffix}`.toLowerCase())) {
        suffix += 1;
    }

    return `${baseName} ${suffix}`;
}

function buildUpsertPayload(draft: DraftProfile): GenerationProfileUpsertParams {
    return {
        name: draft.name.trim(),
        examName: draft.examName,
        sections: sortBySequence(draft.sections).map((section) => ({
            sectionCategory: section.sectionCategory,
            taskTypes: sortBySequence(section.taskTypes).map((group) => ({
                examTaskType: group.examTaskType,
                plannedItemCount: group.plannedItemCount,
            })),
        })),
    };
}

function validateDraft(draft: DraftProfile) {
    if (!draft.name.trim()) {
        return 'Profile name is required.';
    }

    if (draft.sections.length === 0) {
        return 'Add at least one section.';
    }

    if (draft.sections.length > 4) {
        return 'A profile can contain at most four sections.';
    }

    const seenSectionCategories = new Set<string>();
    const seenSectionSequences = new Set<number>();
    for (const section of draft.sections) {
        if (!section.sectionCategory) {
            return 'Each section needs a category.';
        }

        if (section.sequenceNumber <= 0) {
            return `Section ${section.sectionCategory} must have a sequence number greater than zero.`;
        }

        if (seenSectionCategories.has(section.sectionCategory)) {
            return `Section ${section.sectionCategory} is duplicated.`;
        }

        if (seenSectionSequences.has(section.sequenceNumber)) {
            return `Section sequence ${section.sequenceNumber} is duplicated.`;
        }

        seenSectionCategories.add(section.sectionCategory);
        seenSectionSequences.add(section.sequenceNumber);

        if (section.taskTypes.length === 0) {
            return `Section ${section.sectionCategory} must contain at least one exam task type.`;
        }

        const seenTaskTypes = new Set<string>();
        const seenTaskTypeSequences = new Set<number>();
        for (const group of section.taskTypes) {
            if (!group.examTaskType) {
                return `Section ${section.sectionCategory} contains an empty exam task type.`;
            }

            if (group.sequenceNumber <= 0) {
                return `Section ${section.sectionCategory} contains a task type with an invalid sequence number.`;
            }

            if (group.plannedItemCount <= 0) {
                return `Section ${section.sectionCategory} task type ${group.examTaskType} must have a planned count greater than zero.`;
            }

            if (seenTaskTypes.has(group.examTaskType)) {
                return `Section ${section.sectionCategory} has duplicate task type ${group.examTaskType}.`;
            }

            if (seenTaskTypeSequences.has(group.sequenceNumber)) {
                return `Section ${section.sectionCategory} has duplicate task type sequence ${group.sequenceNumber}.`;
            }

            seenTaskTypes.add(group.examTaskType);
            seenTaskTypeSequences.add(group.sequenceNumber);
        }
    }

    return null;
}

export function MockTestGenerationProfilesPage() {
    const notify = useNotify();

    const [examFilter, setExamFilter] = useState<ExamName>('Core');
    const [profiles, setProfiles] = useState<GenerationProfileListItem[]>([]);
    const [loadingList, setLoadingList] = useState(false);
    const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
    const [draft, setDraft] = useState<DraftProfile | null>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [saving, setSaving] = useState(false);
    const [duplicating, setDuplicating] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [search, setSearch] = useState('');
    const [dragState, setDragState] = useState<DragState>(null);
    const [dragOverKey, setDragOverKey] = useState<string | null>(null);

    const examTabs = useMemo<SegmentedTabOption[]>(
        () => examNames.map((examName) => ({ value: examName, label: examName })),
        [],
    );

    const filteredProfiles = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        if (!normalizedSearch) {
            return profiles;
        }

        return profiles.filter((profile) => {
            const haystack = [profile.name, profile.examName, String(profile.sectionCount)].join(' ').toLowerCase();
            return haystack.includes(normalizedSearch);
        });
    }, [profiles, search]);

    const selectedProfileSummary = useMemo(
        () => profiles.find((profile) => profile.id === selectedProfileId) ?? null,
        [profiles, selectedProfileId],
    );

    const sortedSections = useMemo(() => (draft ? sortBySequence(draft.sections) : []), [draft]);

    const loadProfiles = async (targetExamName: ExamName, preferredProfileId?: string | null) => {
        setLoadingList(true);
        try {
            const items = await getGenerationProfileList(targetExamName);
            setProfiles(items);

            const nextSelectedId = preferredProfileId && items.some((item) => item.id === preferredProfileId)
                ? preferredProfileId
                : items[0]?.id ?? null;

            setSelectedProfileId(nextSelectedId);
            if (!nextSelectedId) {
                setDraft(emptyDraftProfile(targetExamName));
            }
        } catch (error) {
            setProfiles([]);
            setSelectedProfileId(null);
            setDraft(emptyDraftProfile(targetExamName));
            notify(error instanceof Error ? error.message : 'Failed to load generation profiles', { type: 'error' });
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        void loadProfiles(examFilter);
    }, [examFilter]);

    useEffect(() => {
        if (!selectedProfileId) {
            return;
        }

        let active = true;

        const loadDetail = async () => {
            setLoadingDetail(true);
            try {
                const detail = await getGenerationProfileDetail(selectedProfileId);
                if (!active) return;
                setDraft(toDraftProfile(detail));
            } catch (error) {
                if (!active) return;
                notify(error instanceof Error ? error.message : 'Failed to load generation profile detail', { type: 'error' });
            } finally {
                if (active) {
                    setLoadingDetail(false);
                }
            }
        };

        void loadDetail();

        return () => {
            active = false;
        };
    }, [notify, selectedProfileId]);

    const clearDragState = () => {
        setDragState(null);
        setDragOverKey(null);
    };

    const handleCreateNew = () => {
        setSelectedProfileId(null);
        setDraft(emptyDraftProfile(examFilter));
        clearDragState();
    };

    const handleSave = async () => {
        if (!draft) {
            return;
        }

        const validationError = validateDraft(draft);
        if (validationError) {
            notify(validationError, { type: 'warning' });
            return;
        }

        setSaving(true);
        try {
            const payload = buildUpsertPayload(draft);
            const saved = draft.id
                ? await updateGenerationProfile(draft.id, payload)
                : await createGenerationProfile(payload);

            const savedDraft = toDraftProfile(saved);
            setDraft(savedDraft);
            setExamFilter(savedDraft.examName);
            await loadProfiles(savedDraft.examName, savedDraft.id ?? saved.generationProfileId);
            notify(`Generation profile ${saved.name} saved.`, { type: 'info' });
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to save generation profile', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleDuplicate = async () => {
        if (!draft) {
            return;
        }

        const copyName = buildCopyName(profiles.filter((profile) => profile.examName === draft.examName), draft.name);
        const duplicateDraft = cloneDraftProfile(draft, copyName);
        const validationError = validateDraft(duplicateDraft);
        if (validationError) {
            notify(validationError, { type: 'warning' });
            return;
        }

        setDuplicating(true);
        try {
            const saved = await createGenerationProfile(buildUpsertPayload(duplicateDraft));
            const savedDraft = toDraftProfile(saved);
            setDraft(savedDraft);
            setExamFilter(savedDraft.examName);
            await loadProfiles(savedDraft.examName, savedDraft.id ?? saved.generationProfileId);
            notify(`Generation profile duplicated as ${saved.name}.`, { type: 'info' });
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to duplicate generation profile', { type: 'error' });
        } finally {
            setDuplicating(false);
        }
    };

    const handleDelete = async () => {
        if (!draft?.id) {
            handleCreateNew();
            return;
        }

        setDeleting(true);
        try {
            await deleteGenerationProfile(draft.id);
            notify(`Generation profile ${draft.name} deleted.`, { type: 'info' });
            await loadProfiles(examFilter);
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to delete generation profile', { type: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    const setSectionValue = (sectionLocalId: string, updater: (section: DraftSection) => DraftSection) => {
        setDraft((current) => {
            if (!current) return current;
            return {
                ...current,
                sections: current.sections.map((section) => (section.localId === sectionLocalId ? updater(section) : section)),
            };
        });
    };

    const setTaskTypeValue = (
        sectionLocalId: string,
        groupLocalId: string,
        updater: (group: DraftGroup, section: DraftSection, profile: DraftProfile) => DraftGroup,
    ) => {
        setDraft((current) => {
            if (!current) return current;
            return {
                ...current,
                sections: current.sections.map((section) => {
                    if (section.localId !== sectionLocalId) {
                        return section;
                    }

                    return {
                        ...section,
                        taskTypes: section.taskTypes.map((group) =>
                            group.localId === groupLocalId ? updater(group, section, current) : group,
                        ),
                    };
                }),
            };
        });
    };

    const moveSection = (fromLocalId: string, toLocalId: string) => {
        setDraft((current) => {
            if (!current) {
                return current;
            }

            const orderedSections = sortBySequence(current.sections);
            const fromIndex = orderedSections.findIndex((section) => section.localId === fromLocalId);
            const toIndex = orderedSections.findIndex((section) => section.localId === toLocalId);
            if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
                return current;
            }

            return {
                ...current,
                sections: resequenceSections(moveItem(orderedSections, fromIndex, toIndex)),
            };
        });
    };

    const moveGroup = (sectionLocalId: string, fromGroupLocalId: string, toGroupLocalId: string) => {
        setDraft((current) => {
            if (!current) {
                return current;
            }

            return {
                ...current,
                sections: current.sections.map((section) => {
                    if (section.localId !== sectionLocalId) {
                        return section;
                    }

                    const orderedGroups = sortBySequence(section.taskTypes);
                    const fromIndex = orderedGroups.findIndex((group) => group.localId === fromGroupLocalId);
                    const toIndex = orderedGroups.findIndex((group) => group.localId === toGroupLocalId);
                    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
                        return section;
                    }

                    return {
                        ...section,
                        taskTypes: resequenceGroups(moveItem(orderedGroups, fromIndex, toIndex)),
                    };
                }),
            };
        });
    };

    const handleSectionDragStart = (event: ReactDragEvent, sectionLocalId: string) => {
        event.dataTransfer.effectAllowed = 'move';
        setDragState({ type: 'section', sectionLocalId });
        setDragOverKey(`section:${sectionLocalId}`);
    };

    const handleGroupDragStart = (event: ReactDragEvent, sectionLocalId: string, groupLocalId: string) => {
        event.stopPropagation();
        event.dataTransfer.effectAllowed = 'move';
        setDragState({ type: 'group', sectionLocalId, groupLocalId });
        setDragOverKey(`group:${sectionLocalId}:${groupLocalId}`);
    };

    return (
        <Card sx={{ width: '100%' }}>
            <Title title="Mock Test Generation Profiles" />
            <CardContent>
                <Stack spacing={3}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                        <Box>
                            <Typography variant="h5" gutterBottom>
                                Mock Test Generation Profiles
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Configure named section order, exam task type order inside each section, and planned counts for mock generation.
                            </Typography>
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshOutlinedIcon />}
                                onClick={() => {
                                    void loadProfiles(examFilter, selectedProfileId);
                                }}
                                disabled={loadingList}
                            >
                                Refresh
                            </Button>
                            <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={handleCreateNew}>
                                New Profile
                            </Button>
                        </Stack>
                    </Stack>

                    <SegmentedTabs
                        label="Exam"
                        value={examFilter}
                        options={examTabs}
                        onChange={(nextValue) => {
                            setExamFilter(nextValue as ExamName);
                            setSearch('');
                        }}
                        helperText="Profiles are stored per exam and can be reused during mock generation."
                    />

                    <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="stretch">
                        <Card variant="outlined" sx={{ width: { xs: '100%', xl: 360 }, flexShrink: 0 }}>
                            <CardContent>
                                <Stack spacing={2}>
                                    <TextField
                                        label="Search Profiles"
                                        size="small"
                                        fullWidth
                                        value={search}
                                        onChange={(event) => setSearch(event.target.value)}
                                    />

                                    {loadingList ? (
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <CircularProgress size={20} />
                                            <Typography variant="body2" color="text.secondary">
                                                Loading profiles...
                                            </Typography>
                                        </Stack>
                                    ) : filteredProfiles.length === 0 ? (
                                        <Alert severity="info">No generation profiles found for {examFilter}.</Alert>
                                    ) : (
                                        <Stack spacing={1}>
                                            {filteredProfiles.map((profile) => {
                                                const isSelected = profile.id === selectedProfileId;
                                                return (
                                                    <Button
                                                        key={profile.id}
                                                        variant={isSelected ? 'contained' : 'outlined'}
                                                        color={isSelected ? 'primary' : 'inherit'}
                                                        onClick={() => setSelectedProfileId(profile.id)}
                                                        sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.25 }}
                                                    >
                                                        <Stack spacing={0.5} sx={{ alignItems: 'flex-start', width: '100%' }}>
                                                            <Typography variant="body2" fontWeight={700}>
                                                                {profile.name}
                                                            </Typography>
                                                            <Typography variant="caption" color={isSelected ? 'inherit' : 'text.secondary'}>
                                                                Sections: {profile.sectionCount}
                                                            </Typography>
                                                            <Typography variant="caption" color={isSelected ? 'inherit' : 'text.secondary'}>
                                                                Updated: {formatDateTime(profile.updatedAt)}
                                                            </Typography>
                                                        </Stack>
                                                    </Button>
                                                );
                                            })}
                                        </Stack>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>

                        <Card variant="outlined" sx={{ flex: 1, minWidth: 0 }}>
                            <CardContent>
                                {loadingDetail ? (
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <CircularProgress size={20} />
                                        <Typography variant="body2" color="text.secondary">
                                            Loading profile detail...
                                        </Typography>
                                    </Stack>
                                ) : !draft ? (
                                    <Alert severity="info">Select a generation profile or create a new one.</Alert>
                                ) : (
                                    <Stack spacing={3}>
                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                                            <Stack spacing={2} sx={{ flex: 1 }}>
                                                <TextField
                                                    label="Profile Name"
                                                    value={draft.name}
                                                    onChange={(event) => {
                                                        const nextValue = event.target.value;
                                                        setDraft((current) => (current ? { ...current, name: nextValue } : current));
                                                    }}
                                                    fullWidth
                                                />
                                            </Stack>

                                            <Stack spacing={1} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<SaveOutlinedIcon />}
                                                    onClick={() => {
                                                        void handleSave();
                                                    }}
                                                    disabled={saving}
                                                >
                                                    {saving ? 'Saving...' : 'Save Profile'}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<ContentCopyOutlinedIcon />}
                                                    onClick={() => {
                                                        void handleDuplicate();
                                                    }}
                                                    disabled={duplicating}
                                                >
                                                    {duplicating ? 'Duplicating...' : 'Duplicate Profile'}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<DeleteOutlineIcon />}
                                                    onClick={() => {
                                                        void handleDelete();
                                                    }}
                                                    disabled={deleting}
                                                >
                                                    {draft.id ? 'Delete Profile' : 'Discard Draft'}
                                                </Button>
                                            </Stack>
                                        </Stack>

                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                            <Chip label={draft.id ? `Profile Id: ${draft.id}` : 'New unsaved profile'} variant="outlined" />
                                            <Chip label={`Created: ${formatDateTime(draft.createdAt)}`} variant="outlined" />
                                            <Chip label={`Updated: ${formatDateTime(draft.updatedAt)}`} variant="outlined" />
                                        </Stack>

                                        <Alert severity="info">
                                            You can edit numeric sequence fields directly or drag sections and task types to reorder them. Drag-and-drop immediately resequences the list to match the new order.
                                        </Alert>

                                        <Divider />

                                        <Stack spacing={2}>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                                <Box>
                                                    <Typography variant="h6">Sections</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Define section order for full mocks and per-section task ordering for sectional generation.
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<AddCircleOutlineIcon />}
                                                    disabled={draft.sections.length >= examTaskCategories.length}
                                                    onClick={() => {
                                                        setDraft((current) =>
                                                            current
                                                                ? {
                                                                      ...current,
                                                                      sections: [...current.sections, createDraftSection(current.examName, current.sections)],
                                                                  }
                                                                : current,
                                                        );
                                                    }}
                                                >
                                                    Add Section
                                                </Button>
                                            </Stack>

                                            {sortedSections.map((section) => {
                                                const allowedTaskTypes = getAllowedTaskTypes(draft.examName, section.sectionCategory);
                                                const usedTaskTypes = new Set(section.taskTypes.map((group) => group.examTaskType));
                                                const canAddTaskType = allowedTaskTypes.some((taskType) => !usedTaskTypes.has(taskType.apiType));
                                                const sectionCategoryOptions = examTaskCategories.filter(
                                                    (category) =>
                                                        category.id === section.sectionCategory ||
                                                        !draft.sections.some((entry) => entry.sectionCategory === category.id),
                                                );
                                                const sectionDragKey = `section:${section.localId}`;
                                                const sortedGroups = sortBySequence(section.taskTypes);

                                                return (
                                                    <Card
                                                        key={section.localId}
                                                        variant="outlined"
                                                        onDragOver={(event) => {
                                                            if (dragState?.type !== 'section') {
                                                                return;
                                                            }

                                                            event.preventDefault();
                                                            setDragOverKey(sectionDragKey);
                                                        }}
                                                        onDrop={(event) => {
                                                            if (dragState?.type !== 'section') {
                                                                return;
                                                            }

                                                            event.preventDefault();
                                                            moveSection(dragState.sectionLocalId, section.localId);
                                                            clearDragState();
                                                        }}
                                                        onDragEnd={clearDragState}
                                                        sx={{
                                                            borderColor: dragOverKey === sectionDragKey ? 'primary.main' : 'divider',
                                                            boxShadow: dragOverKey === sectionDragKey ? 3 : 'none',
                                                        }}
                                                    >
                                                        <CardContent>
                                                            <Stack spacing={2.5}>
                                                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                                        <Tooltip title="Drag to reorder section">
                                                                            <IconButton
                                                                                draggable
                                                                                onDragStart={(event) => handleSectionDragStart(event, section.localId)}
                                                                                onDragEnd={clearDragState}
                                                                                sx={{ cursor: 'grab' }}
                                                                            >
                                                                                <DragIndicatorOutlinedIcon />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Typography variant="h6">{section.sectionCategory} Section</Typography>
                                                                    </Stack>
                                                                    <Button
                                                                        variant="outlined"
                                                                        color="error"
                                                                        startIcon={<DeleteOutlineIcon />}
                                                                        onClick={() => {
                                                                            setDraft((current) =>
                                                                                current
                                                                                    ? {
                                                                                          ...current,
                                                                                          sections: current.sections.filter((entry) => entry.localId !== section.localId),
                                                                                      }
                                                                                    : current,
                                                                            );
                                                                        }}
                                                                    >
                                                                        Remove Section
                                                                    </Button>
                                                                </Stack>

                                                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                                                    <TextField
                                                                        label="Section Sequence"
                                                                        type="number"
                                                                        value={section.sequenceNumber}
                                                                        inputProps={{ min: 1 }}
                                                                        onChange={(event) => {
                                                                            const nextValue = Number(event.target.value);
                                                                            setSectionValue(section.localId, (current) => ({
                                                                                ...current,
                                                                                sequenceNumber: Number.isFinite(nextValue) && nextValue > 0 ? nextValue : 1,
                                                                            }));
                                                                        }}
                                                                        sx={{ width: { xs: '100%', md: 180 } }}
                                                                    />
                                                                    <TextField
                                                                        label="Section Category"
                                                                        select
                                                                        value={section.sectionCategory}
                                                                        onChange={(event) => {
                                                                            const nextCategory = event.target.value;
                                                                            setSectionValue(section.localId, (current) => ({
                                                                                ...current,
                                                                                sectionCategory: nextCategory,
                                                                                taskTypes: normalizeSectionsForExamName([
                                                                                    {
                                                                                        ...current,
                                                                                        sectionCategory: nextCategory,
                                                                                    },
                                                                                ], draft.examName)[0].taskTypes,
                                                                            }));
                                                                        }}
                                                                        sx={{ minWidth: 220 }}
                                                                    >
                                                                        {sectionCategoryOptions.map((category) => (
                                                                            <MenuItem key={category.id} value={category.id}>
                                                                                {category.label}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </TextField>
                                                                </Stack>

                                                                <Divider />

                                                                <Stack spacing={1.5}>
                                                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                                                        <Typography variant="subtitle1">Exam Task Types</Typography>
                                                                        <Button
                                                                            variant="outlined"
                                                                            startIcon={<AddCircleOutlineIcon />}
                                                                            disabled={!canAddTaskType}
                                                                            onClick={() => {
                                                                                setSectionValue(section.localId, (current) => ({
                                                                                    ...current,
                                                                                    taskTypes: [
                                                                                        ...current.taskTypes,
                                                                                        createDraftGroup(draft.examName, current.sectionCategory, current.taskTypes),
                                                                                    ],
                                                                                }));
                                                                            }}
                                                                        >
                                                                            Add Task Type
                                                                        </Button>
                                                                    </Stack>

                                                                    {sortedGroups.map((group) => {
                                                                        const availableTaskTypes = allowedTaskTypes.filter(
                                                                            (taskType) =>
                                                                                taskType.apiType === group.examTaskType ||
                                                                                !section.taskTypes.some((entry) => entry.examTaskType === taskType.apiType),
                                                                        );
                                                                        const groupDragKey = `group:${section.localId}:${group.localId}`;

                                                                        return (
                                                                            <Stack
                                                                                key={group.localId}
                                                                                direction={{ xs: 'column', lg: 'row' }}
                                                                                spacing={1.5}
                                                                                alignItems={{ xs: 'stretch', lg: 'center' }}
                                                                                onDragOver={(event) => {
                                                                                    if (dragState?.type !== 'group' || dragState.sectionLocalId !== section.localId) {
                                                                                        return;
                                                                                    }

                                                                                    event.preventDefault();
                                                                                    event.stopPropagation();
                                                                                    setDragOverKey(groupDragKey);
                                                                                }}
                                                                                onDrop={(event) => {
                                                                                    if (dragState?.type !== 'group' || dragState.sectionLocalId !== section.localId) {
                                                                                        return;
                                                                                    }

                                                                                    event.preventDefault();
                                                                                    event.stopPropagation();
                                                                                    moveGroup(section.localId, dragState.groupLocalId, group.localId);
                                                                                    clearDragState();
                                                                                }}
                                                                                sx={{
                                                                                    border: 1,
                                                                                    borderColor: dragOverKey === groupDragKey ? 'primary.main' : 'divider',
                                                                                    borderRadius: 2,
                                                                                    px: 1.25,
                                                                                    py: 1.25,
                                                                                    backgroundColor: dragOverKey === groupDragKey ? 'action.selected' : 'transparent',
                                                                                }}
                                                                            >
                                                                                <Tooltip title="Drag to reorder task type">
                                                                                    <IconButton
                                                                                        draggable
                                                                                        onDragStart={(event) => handleGroupDragStart(event, section.localId, group.localId)}
                                                                                        onDragEnd={clearDragState}
                                                                                        sx={{ cursor: 'grab' }}
                                                                                    >
                                                                                        <DragIndicatorOutlinedIcon />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                                <TextField
                                                                                    label="Sequence"
                                                                                    type="number"
                                                                                    value={group.sequenceNumber}
                                                                                    inputProps={{ min: 1 }}
                                                                                    onChange={(event) => {
                                                                                        const nextValue = Number(event.target.value);
                                                                                        setTaskTypeValue(section.localId, group.localId, (current) => ({
                                                                                            ...current,
                                                                                            sequenceNumber: Number.isFinite(nextValue) && nextValue > 0 ? nextValue : 1,
                                                                                        }));
                                                                                    }}
                                                                                    sx={{ width: { xs: '100%', lg: 140 } }}
                                                                                />
                                                                                <TextField
                                                                                    label="Exam Task Type"
                                                                                    select
                                                                                    value={group.examTaskType}
                                                                                    onChange={(event) => {
                                                                                        const nextValue = event.target.value;
                                                                                        setTaskTypeValue(section.localId, group.localId, (current) => ({
                                                                                            ...current,
                                                                                            examTaskType: nextValue,
                                                                                        }));
                                                                                    }}
                                                                                    sx={{ flex: 1, minWidth: 280 }}
                                                                                >
                                                                                    {availableTaskTypes.map((taskType) => (
                                                                                        <MenuItem key={taskType.apiType} value={taskType.apiType}>
                                                                                            {getExamTaskTypeLabel(taskType, draft.examName)}
                                                                                        </MenuItem>
                                                                                    ))}
                                                                                </TextField>
                                                                                <TextField
                                                                                    label="Planned Count"
                                                                                    type="number"
                                                                                    value={group.plannedItemCount}
                                                                                    inputProps={{ min: 1 }}
                                                                                    onChange={(event) => {
                                                                                        const nextValue = Number(event.target.value);
                                                                                        setTaskTypeValue(section.localId, group.localId, (current) => ({
                                                                                            ...current,
                                                                                            plannedItemCount: Number.isFinite(nextValue) && nextValue > 0 ? nextValue : 1,
                                                                                        }));
                                                                                    }}
                                                                                    sx={{ width: { xs: '100%', lg: 160 } }}
                                                                                />
                                                                                <IconButton
                                                                                    color="error"
                                                                                    aria-label="Remove task type"
                                                                                    onClick={() => {
                                                                                        setSectionValue(section.localId, (current) => ({
                                                                                            ...current,
                                                                                            taskTypes: current.taskTypes.filter((entry) => entry.localId !== group.localId),
                                                                                        }));
                                                                                    }}
                                                                                >
                                                                                    <DeleteOutlineIcon />
                                                                                </IconButton>
                                                                            </Stack>
                                                                        );
                                                                    })}
                                                                </Stack>
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>
                                                );
                                            })}
                                        </Stack>
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>
                    </Stack>

                    {selectedProfileSummary ? (
                        <Typography variant="body2" color="text.secondary">
                            Selected profile: {selectedProfileSummary.name} for {selectedProfileSummary.examName}.
                        </Typography>
                    ) : null}
                </Stack>
            </CardContent>
        </Card>
    );
}
